import ExploreServiceLocation from '../types/ExploreServiceLocation';

const restrictLat = (lat: number) => Math.max(Math.min(lat, 90), -90);

const restrictCoordinates = ({ lat, lng }: { lat: number; lng: number }) => {
  let updatedLng = lng;
  if (lng > 180) {
    updatedLng = lng - 360;
  } else if (lng < -180) {
    updatedLng = lng + 360;
  }
  return { lat: restrictLat(lat), lng: updatedLng };
};

/**
 * Updates lat/lng coordinates of a location bounding box to be within acceptable ranges.
 *
 * If the map is panned just to the left of the dateline, mapbox return lng values above 180 degrees for right edge of the bounding box.
 * If the map is panned just to the right of the dateline, mapbox return lng values below -180 degrees for left edge of the bounding box.
 * If the map is zoomed all the way out such that the map starts repeating, returns a lng range greater than 360 degrees.
 * All of these are issues since the explore-service expects lng values to be within -180 to 180 degrees.
 *
 * Also restricts lat values to -90 to 90 degrees, which has not been an issue, but is what the explore-service expects.
 * @param location - ExploreServiceLocation
 * @returns - ExploreServiceLocation with lng restricted to -180 to 180 degrees
 */
const cleanExploreServicePayloadLocation = (location?: ExploreServiceLocation) => {
  if (!location) {
    return undefined;
  }

  if ('bottomLeft' in location) {
    const cleanedLocation = { ...location };
    // We are requesting the entire lng range, update to -180 to 180 degrees
    if (location.bottomRight.lng - location.bottomLeft.lng > 360) {
      cleanedLocation.bottomLeft = { lat: restrictLat(location.bottomLeft.lat), lng: -180 };
      cleanedLocation.bottomRight = { lat: restrictLat(location.bottomRight.lat), lng: 180 };
      cleanedLocation.topLeft = { lat: restrictLat(location.topLeft.lat), lng: -180 };
      cleanedLocation.topRight = { lat: restrictLat(location.topRight.lat), lng: 180 };
    } else {
      cleanedLocation.bottomLeft = restrictCoordinates(cleanedLocation.bottomLeft);
      cleanedLocation.bottomRight = restrictCoordinates(cleanedLocation.bottomRight);
      cleanedLocation.topLeft = restrictCoordinates(cleanedLocation.topLeft);
      cleanedLocation.topRight = restrictCoordinates(cleanedLocation.topRight);
    }
    return cleanedLocation;
  }

  return location;
};

export default cleanExploreServicePayloadLocation;
